/**
 * Assessment AI Service for AdapTeach
 * Uses direct AI API calls without Supabase Edge Functions
 */

import { logError, formatErrorForUser } from "./errorHandling";
import {
  generateAssessment as generateAssessmentSumoPod,
  AssessmentRequest,
  AssessmentResponse,
} from "./sumpodAIService";

export interface CognitiveDistribution {
  c1c2: number; // Level 1: Mengingat & Memahami (Remember & Understand)
  c3: number; // Level 2: Mengaplikasikan (Apply)
  c4c5c6: number; // Level 3: Menganalisis, Mengevaluasi, Mencipta (Analyze, Evaluate, Create)
}

/**
 * Get default cognitive distribution for assessments
 */
export const getDefaultCognitiveDistribution = (): CognitiveDistribution => {
  return {
    c1c2: 30, // Remember & Understand: 30%
    c3: 40, // Apply: 40%
    c4c5c6: 30, // Analyze, Evaluate, Create: 30%
  };
};

/**
 * Get default question count based on education level
 */
export const getDefaultQuestionCount = (jenjangPendidikan?: string): number => {
  switch (jenjangPendidikan?.toLowerCase()) {
    case "sd":
    case "sekolah dasar":
      return 10;
    case "smp":
    case "sekolah menengah pertama":
      return 15;
    case "sma":
    case "sekolah menengah atas":
      return 20;
    case "sma/smk":
      return 20;
    case "universitas":
    case "perguruan tinggi":
      return 25;
    default:
      return 15;
  }
};

/**
 * Validate assessment request data
 */
export const validateAssessmentRequest = (formData: Record<string, unknown>): boolean => {
  if (!formData.mataPelajaran || formData.mataPelajaran.trim() === "") {
    return false;
  }
  if (!formData.jenjangPendidikan || formData.jenjangPendidikan.trim() === "") {
    return false;
  }
  if (!formData.topikTujuan || formData.topikTujuan.trim() === "") {
    return false;
  }
  if (!formData.tipePertanyaan || formData.tipePertanyaan.trim() === "") {
    return false;
  }
  return true;
};

/**
 * Generate an assessment using SumoPod AI API
 */
export const generateAssessment = async (
  formData: AssessmentRequest
): Promise<AssessmentResponse> => {
  try {
    return await generateAssessmentSumoPod(formData);
  } catch (error) {
    logError("generateAssessment", error, formData);

    // Provide fallback assessment if API fails
    const fallback = createFallbackAssessment(formData);

    return {
      ...fallback,
      fallback: true,
      error: formatErrorForUser(error, "pembuatan asesmen"),
    };
  }
};

/**
 * Create a fallback assessment when API fails
 */
export const createFallbackAssessment = (
  formData: AssessmentRequest
): AssessmentResponse => {
  const title = `Asesmen ${formData.mataPelajaran} (${formData.tipePertanyaan}) - ${formData.jenjangPendidikan?.toUpperCase() || "Jenjang Pendidikan"}`;

  const content = `# ${title}

## INFORMASI ASESMEN
- **Mata Pelajaran**: ${formData.mataPelajaran}
- **Jenjang Pendidikan**: ${formData.jenjangPendidikan}
- **Topik**: ${formData.topikTujuan}
- **Tipe Soal**: ${formData.tipePertanyaan}
- **Jumlah Soal**: ${formData.jumlahSoal || 10}
- **Alokasi Waktu**: ${formData.aloksiWaktu || 60} menit

## PETUNJUK UMUM
1. Bacalah setiap soal dengan teliti sebelum menjawab
2. Kerjakan soal yang dianggap mudah terlebih dahulu
3. Periksa kembali jawaban sebelum mengumpulkan
4. Tuliskan jawaban dengan jelas dan rapi

## SOAL-SOAL

### Soal 1
Jelaskan konsep dasar dari ${formData.topikTujuan}!

### Soal 2
Berikan contoh penerapan ${formData.topikTujuan} dalam kehidupan sehari-hari!

### Soal 3
Analisislah hubungan antara ${formData.topikTujuan} dengan materi ${formData.mataPelajaran} lainnya!

[Soal-soal tambahan akan disesuaikan dengan kebutuhan spesifik]

## KUNCI JAWABAN
1. [Jawaban untuk soal 1 - konsep dasar]
2. [Jawaban untuk soal 2 - contoh penerapan]
3. [Jawaban untuk soal 3 - analisis hubungan]

*Catatan: Ini adalah template dasar yang dapat disesuaikan dengan kebutuhan spesifik asesmen.*`;

  const answerKey = `Kunci Jawaban ${title}:
1. Konsep dasar ${formData.topikTujuan}
2. Contoh penerapan dalam kehidupan sehari-hari
3. Analisis hubungan dengan materi lainnya`;

  return {
    success: true,
    content: content,
    title: title,
    answerKey: answerKey,
    fallback: true,
    provider: "fallback",
  };
};
